import React, { useContext, useRef, useState, } from 'react';
import styled, { css, ThemeContext } from 'styled-components';
import { useMedia } from '../../../hooks/useMedia';
import { HeadShakeAnimation } from '../../../styles/HeadShakeAnimation';
import { MAX_MOBILE_WIDTH_IN_PX, MIN_WIDTH_MAIN_NAVIGATION_IN_PX, } from '../constants';
import { useHighlightSwoosh } from '../hooks/useHighlightSwoosh';
import { useIsMobileNavigation } from '../hooks/useIsMobileNavigation';
import { MainNavigationContext } from '../MainNavigationContext';
import { MobileNavigation } from './MobileNavigation';
import { HEIGHT_VIEWBOX_LARGE_SWUNG_IN_PX, HEIGHT_VIEWBOX_SMALL_SWUNG_IN_PX, SwungBorder, } from './SwungBorder';
const MIN_DISTANCE_LEFT_IN_REM = 2;
const MIN_DISTANCE_RIGHT_IN_REM = 1;
const WIDTH_INNER_CONTAINER_IN_REM = 73; // = 1168px
const WIDTH_GUTTER_IN_REM = 1;
const DEBOUNCE_DELAY_IN_MS = 300;
const ViewportWideWrapper = styled.div `
  align-items: center;
  display: flex;
  flex-direction: column;
  font-family: ${props => props.theme.fonts.serif};
  height: ${HEIGHT_VIEWBOX_LARGE_SWUNG_IN_PX / 16}rem;
  min-width: ${MIN_WIDTH_MAIN_NAVIGATION_IN_PX / 16}rem;
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 1000;

  @media screen and (max-width: ${MAX_MOBILE_WIDTH_IN_PX}px) {
    height: ${HEIGHT_VIEWBOX_SMALL_SWUNG_IN_PX / 16}rem;
  }
`;
const LIMITED_WIDTH_WRAPPER_MIN_WIDTH = WIDTH_INNER_CONTAINER_IN_REM +
    MIN_DISTANCE_LEFT_IN_REM +
    MIN_DISTANCE_RIGHT_IN_REM;
const LIMITED_WIDTH_WRAPPER_MAX_WIDTH = WIDTH_INNER_CONTAINER_IN_REM +
    2 * WIDTH_GUTTER_IN_REM +
    (MIN_DISTANCE_LEFT_IN_REM - MIN_DISTANCE_RIGHT_IN_REM);
const LimitedWidthWrapper = styled.div `
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  max-width: ${LIMITED_WIDTH_WRAPPER_MAX_WIDTH}rem;
  padding-left: ${MIN_DISTANCE_LEFT_IN_REM}rem;
  padding-right: ${MIN_DISTANCE_RIGHT_IN_REM}rem;

  /**
   * The inner container has a maximal width of 1168px + 2 gutters of 16px. However, the logo should have at least 32px to the left viewport edge. Therefore, if the viewport is 1216px (1168 + 16 + 32) or less wide, the horizontal distance to the viewport borders is asymmetric: 32px (2rem) left and 16 (1rem) right so that the logo doesn't collide with the swung. On large screens (>= 1232px), the inner container should be centered. The tough part is everything between 1216px and 1232px as the precise padding-left value depends on the actual viewport width. Therefore, the inner container is moved half the padding difference to the left if the viewport is large enough to still have the 32px distance to the left.
   */
  @media screen and (min-width: ${LIMITED_WIDTH_WRAPPER_MIN_WIDTH}rem) {
    position: relative;
    left: -${(MIN_DISTANCE_LEFT_IN_REM - MIN_DISTANCE_RIGHT_IN_REM) / 2}rem;
  }
`;
const LogoSlot = styled.div `
  height: 100%;
  padding-top: 1rem;
  margin-right: 2.5rem;

  @media screen and (max-width: ${MAX_MOBILE_WIDTH_IN_PX}px) {
    /* Bottom: Harmonize distance to top/border edge to be equal by subtracting the padding value */
    padding-top: 0.5rem;

    /* Distance to other elements is defined by other styles */
    margin-right: 0;
  }

  ${props => props.isLogoAnimated &&
    css `
      &:hover {
        animation: ${HeadShakeAnimation} 2s;

        @media screen and (prefers-reduced-motion: reduce) {
          animation: none;
        }
      }
    `}
`;
const PrimarySlot = styled.div `
  align-items: center;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: 100%;

  /* Bottom: Harmonize distance to top/border edge to be equal by subtracting the padding value */
  padding-bottom: 0.75rem;

  @media screen and (max-width: ${MAX_MOBILE_WIDTH_IN_PX}px) {
    height: auto;
    padding-bottom: 0;
  }
`;
const SecondarySlot = styled.div `
  align-items: center;
  display: flex;
  flex-direction: column;
  font-size: 0.75rem;
  height: 100%;
  justify-content: center;

  /* Bottom: Harmonize distance to top/border edge to be equal by subtracting the padding value */
  padding-bottom: 1rem;

  @media screen and (max-width: ${MAX_MOBILE_WIDTH_IN_PX}px) {
    height: auto;
    padding-bottom: 0;
  }
`;
const MobileSlot = styled.div `
  align-items: start;
  display: flex;
  font-size: 0.8125rem;
  margin-left: auto;
  padding-top: 0.5rem;
`;
export const MainNavigation = ({ className, hasHamburgerMenu = true, isLoggedIn = false, isLogoAnimated = true, isMobileMenuOpen = false, logo, mobile = null, onChangeIsMobileMenuOpen = () => undefined, primary = null, hamburgerMenuLoginOut = null, secondary = null, mobileTrackingId = undefined, }) => {
    const theme = useContext(ThemeContext);
    const isLaptopLWidth = useMedia({ minWidth: theme.screenLaptopL });
    const isMobile = useIsMobileNavigation();
    const [isNestedNavigationOpen, setIsNestedNavigationOpen] = useState(false);
    const [shouldFocusNestedNavigation, setShouldFocusNestedNavigation] = useState(true);
    /**
     * Element reference used in »useHighlightSwoosh«
     * This element will be observed by the ResizeObserver
     */
    const viewportWideWrapperRef = useRef(null);
    /**
     * This hook calculates the highlight position for »SwungBorder«
     * and returns
     * * Callbacks for handling the navigation items to highlight
     * * The current position of the highlight
     * * And the current width of the navigation
     */
    const { registerNavigationItem, unregisterNavigationItem, updateActiveNavigationItem, highlightPosition, navigationWidth, } = useHighlightSwoosh(viewportWideWrapperRef, DEBOUNCE_DELAY_IN_MS);
    return (React.createElement(ViewportWideWrapper, { ref: viewportWideWrapperRef, "data-testid": "main-navigation", className: className },
        React.createElement(MainNavigationContext.Provider, { value: {
                distanceBetweenPrimaryNavigationItemsInRem: isLaptopLWidth
                    ? 2.5
                    : 1.5,
                isLoggedIn,
                isMobileMenuOpen,
                isNestedNavigationOpen,
                registerNavigationItem,
                setIsNestedNavigationOpen,
                setShouldFocusNestedNavigation,
                shouldFocusNestedNavigation,
                unregisterNavigationItem,
                updateActiveNavigationItem,
            } },
            React.createElement(LimitedWidthWrapper, null,
                React.createElement(LogoSlot, { "data-testid": "main-navigation-logo-slot", isLogoAnimated: isLogoAnimated }, logo),
                isMobile ? (React.createElement(MobileSlot, { "data-testid": "main-navigation-mobile-slot" },
                    mobile,
                    hasHamburgerMenu && (React.createElement(MobileNavigation, { isLoggedIn: isLoggedIn, "data-trackingid": mobileTrackingId, isMobileMenuOpen: isMobileMenuOpen, onChangeIsMobileMenuOpen: onChangeIsMobileMenuOpen, primaryItems: React.createElement(PrimarySlot, { "data-testid": "main-navigation-primary-slot-mobile" }, primary), secondaryItems: React.createElement(SecondarySlot, { "data-testid": "main-navigation-secondary-slot-mobile" }, secondary), hamburgerMenuLoginOutSlot: hamburgerMenuLoginOut ? (React.createElement(PrimarySlot, { "data-testid": "hamburger-menu-login-logout-slot" }, hamburgerMenuLoginOut)) : null })))) : (React.createElement(React.Fragment, null,
                    React.createElement(PrimarySlot, { "data-testid": "main-navigation-primary-slot-desktop" }, primary),
                    React.createElement(SecondarySlot, { "data-testid": "main-navigation-secondary-slot-desktop" }, secondary)))),
            React.createElement(SwungBorder, { highlightPosition: highlightPosition, navigationWidth: navigationWidth }))));
};
